import { db } from 'api/Firebase/firebase-init'
import { ButtonLoading } from 'components/Buttons'
import { Container } from 'components/Container'
import { PacmanSpinner } from 'components/Spinner'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useActiveTickets } from 'store/useActiveTickets'
import { useCurrentUser } from 'store/useCurrentUser'
import { useForms } from 'store/useForms'
import { useInventories } from 'store/useInventories'
import { useMessagingToken } from 'store/useMessagingToken'
import { usePlace } from 'store/usePlace'
import { useQueues } from 'store/useQueues'
import { useServices } from 'store/useServices'
import { useWizardForm } from 'store/useWizardForm'
import FormattedMessage from 'utils/FormattedMessage'
import { isIOS, isInstalledPWA } from 'utils/isMobile'
import { useTranslation } from 'utils/useTranslation'
import {
  CategoryStep,
  ChoiceStep,
  ConfirmAppointment,
  ContactsStep,
  FormHeader,
  FormsStep,
  PausedQueueWarning,
  Products,
  ServiceStep,
  SlotTakenWarning,
  TicketSettingModal,
  TimeSlotStep,
  TimeZonePopup,
  TooManyPeopleWarning,
} from './components'
import { PushNotification } from './components/PushNotification'
import {
  CATEGORY_STEP,
  CHOICE_STEP,
  CONTACTS_STEP,
  FORMS_STEPS,
  FORMS_STEP_1,
  FORMS_STEP_2,
  FORMS_STEP_3,
  FORMS_STEP_4,
  FORMS_STEP_5,
  PRODUCTS_STEP,
  SERVICE_STEP,
  TIMESLOT_STEP,
  useWizardSteps,
} from './useWizardSteps'
import useQueueStats from 'store/useQueueStats'
import { useQueueTickets } from 'store/useQueueTickets'

export const BookPage = () => {
  const intl = useTranslation()
  const { placeId: urlPlaceId } = useParams()
  const { services, subscribeToServices, unsubscribeFromServices, isLoadingServices } =
    useServices()
  const { place, subscribeToPlace, unsubscribeFromPlace, loaded: placeLoaded } = usePlace()
  const { subscribeToInventory, unsubscribeFromInventory } = useInventories()
  const { subscribeToQueues, unsubscribeFromQueues } = useQueues()
  const { subscribeToForms, unsubscribeFromForms } = useForms()
  const { wizardForm, updateWizardForm, resetWizardForm, setPushToken } = useWizardForm()
  const { ensureToken: refreshToken } = useMessagingToken()
  const [isNextStepEnabled, setIsNextStepEnabled] = useState(false)
  const [isContactDataValid, setIsContactDataValid] = useState(true)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const [isSlotTaken, setIsSlotTaken] = useState(false)
  const slotSubscriptionRef = useRef<(() => void) | null>(null)
  const { subscribeToQueueStats, unsubscribeFromQueueStats } = useQueueStats()
  const { subscribeToQueueTickets, unsubscribeFromQueueTickets } = useQueueTickets()
  const { isQueueDisabled } = useWizardSteps()
  const uid = useCurrentUser(s => s.authUser?.uid)

  const { state } = useLocation()
  const isItKiosk = state && state.kioskMode

  const {
    availableSteps,
    form,
    queue,
    activeStep,
    activeStepIndex,
    goToNextStep,
    goToPrevStep,
    isFirstStep,
    isLastStep,
  } = useWizardSteps()

  useEffect(() => {
    const unsubscribe = () => {
      unsubscribeFromPlace()
      unsubscribeFromServices()
      unsubscribeFromForms()
      unsubscribeFromInventory()
      unsubscribeFromQueues()
    }
    if (urlPlaceId) {
      subscribeToPlace(urlPlaceId)
      subscribeToServices(urlPlaceId)
      subscribeToForms(urlPlaceId)
      subscribeToInventory(urlPlaceId)
      subscribeToQueues(urlPlaceId)

      updateWizardForm({ placeId: urlPlaceId })
    }
    return unsubscribe
  }, [urlPlaceId])

  useEffect(() => {
    if (urlPlaceId && uid) {
      useActiveTickets.getState().subscribe(urlPlaceId, uid)
    }
    return () => useActiveTickets.getState().unsubscribe()
  }, [urlPlaceId, uid])

  useEffect(() => {
    if (urlPlaceId && queue?.id) {
      subscribeToQueueTickets(urlPlaceId, queue?.id)
      subscribeToQueueStats(urlPlaceId, queue?.id)
    }

    return () => {
      unsubscribeFromQueueStats()
      unsubscribeFromQueueTickets()
    }
  }, [urlPlaceId, queue?.id])

  useEffect(() => {
    if (place?.id && wizardForm?.placeId && place.id !== wizardForm.placeId) {
      resetWizardForm(place.id)
    }
  }, [place?.id, resetWizardForm, wizardForm.placeId])

  // If notification permission is already granted and the place has push enabled,
  // silently refresh the token and write it into the booking form.
  // This covers iOS PWA users and returning users without ever showing the modal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      !place?.messagingConfig.useWebPushMessenger ||
      isItKiosk ||
      (isIOS() && !isInstalledPWA()) ||
      typeof Notification === 'undefined' ||
      Notification.permission !== 'granted'
    ) {
      return
    }
    refreshToken().then(token => {
      setPushToken(token)
    })
  }, [place?.id])

  // Live slot availability: subscribe to active bookings for the selected slot.
  // If occupancy reaches capacity while the user is mid-flow, show a blocking modal.
  useEffect(() => {
    // Clean up any previous subscription
    if (slotSubscriptionRef.current) {
      slotSubscriptionRef.current()
      slotSubscriptionRef.current = null
    }

    const { selectedTime, placeId, scheduleId } = wizardForm
    if (!selectedTime?.startTime || !selectedTime?.endTime || !placeId || !scheduleId) {
      setIsSlotTaken(false)
      return
    }

    const startDate = new Date(selectedTime.startTime)
    const endDate = new Date(selectedTime.endTime)
    const capacity = selectedTime.shopCapacity

    const ref = query(
      collection(db, `places/${placeId}/tickets_short`),
      where('scheduleId', '==', scheduleId),
      where('startTime', '>=', startDate),
      where('startTime', '<', endDate),
      where('status', 'in', ['created', 'queued', 'invited', 'processed'])
    )

    const unsubscribe = onSnapshot(
      ref,
      snap => {
        setIsSlotTaken(snap.size >= capacity)
      },
      error => {
        console.error('Slot availability subscription failed', error)
      }
    )

    slotSubscriptionRef.current = unsubscribe
    return () => {
      unsubscribe()
      slotSubscriptionRef.current = null
    }
  }, [
    wizardForm.selectedTime?.startTime,
    wizardForm.selectedTime?.endTime,
    wizardForm.placeId,
    wizardForm.scheduleId,
  ])

  useEffect(() => {
    function isPageValid(pageId?: string) {
      if (!pageId) return false
      return Boolean(wizardForm.form?.pages[pageId]?.valid)
    }

    const stepChecks = {
      [CATEGORY_STEP]: () => !!wizardForm.serviceCategoryId || !!wizardForm.serviceId,
      [SERVICE_STEP]: () => !!wizardForm.serviceId,
      [CHOICE_STEP]: () => (!!wizardForm.queueId && !isQueueDisabled) || !!wizardForm.scheduleId,
      [TIMESLOT_STEP]: () => !!wizardForm.selectedTime?.label,
      [CONTACTS_STEP]: () => isContactDataValid,
      [FORMS_STEP_1]: () => isPageValid(form?.pages[0].id),
      [FORMS_STEP_2]: () => isPageValid(form?.pages[1].id),
      [FORMS_STEP_3]: () => isPageValid(form?.pages[2].id),
      [FORMS_STEP_4]: () => isPageValid(form?.pages[3].id),
      [FORMS_STEP_5]: () => isPageValid(form?.pages[4].id),
      [PRODUCTS_STEP]: () => true,
    }

    const isStepValid = stepChecks[activeStep] ? stepChecks[activeStep]() : false
    setIsNextStepEnabled(isStepValid)
  }, [isContactDataValid, wizardForm, activeStep, isQueueDisabled])

  const onNextStepClicked = () => {
    if (isLastStep) {
      if (
        !(isIOS() && !isInstalledPWA()) &&
        !isItKiosk &&
        place?.messagingConfig.useWebPushMessenger &&
        typeof Notification !== 'undefined' &&
        Notification.permission === 'default' &&
        !wizardForm.contactData.pushToken
      ) {
        setShowNotificationModal(true)
      } else {
        setShowConfirmModal(true)
      }
    }
    goToNextStep()
  }

  const onPrevStepClicked = () => {
    goToPrevStep()
  }

  return (
    <>
      {!placeLoaded || isLoadingServices ? (
        <div className='flex justify-center items-center h-screen'>
          <PacmanSpinner />
        </div>
      ) : (
        <>
          <FormHeader goToPreviousStep={onPrevStepClicked} />
          <Container>
            <div className='flex justify-between h-full flex-col mt-5'>
              {(services.length > 0 && (
                <>
                  <div className='md:mb-1'>
                    {activeStep === CATEGORY_STEP && <CategoryStep />}
                    {activeStep === SERVICE_STEP && <ServiceStep />}
                    {activeStep === CHOICE_STEP && <ChoiceStep skipStep={onNextStepClicked} />}
                    {activeStep === TIMESLOT_STEP && <TimeSlotStep />}
                    {activeStep === CONTACTS_STEP && (
                      <ContactsStep onValid={setIsContactDataValid} />
                    )}
                    {FORMS_STEPS.includes(activeStep) && form && (
                      <FormsStep
                        formPage={
                          form?.pages[activeStepIndex - availableSteps.indexOf(FORMS_STEP_1)]
                        }
                      />
                    )}
                    {activeStep === PRODUCTS_STEP && <Products />}
                  </div>
                  <div
                    className={`fixed bottom-[56px] w-[calc(100%-32px)] md:static md:w-full md:flex md:h-auto md:pb-6 pt-2 md:pt-4 ${
                      !isFirstStep ? 'md:justify-between' : 'md:justify-end'
                    } border-none h-[80px]  `}
                  >
                    {!isFirstStep && (
                      <ButtonLoading
                        onClick={onPrevStepClicked}
                        style='w-full hidden !mb-0 md:block md:w-auto border-solid border-1 border-gray-300 bg-gray-100 !text-gray-900 dark:bg-gray-600 dark:border-gray-500 dark:!text-gray-100'
                      >
                        {intl({
                          id: 'back',
                        })}
                      </ButtonLoading>
                    )}
                    <ButtonLoading
                      disabled={!isNextStepEnabled}
                      onClick={onNextStepClicked}
                      style='w-full !mb-0 md:w-auto border-none'
                    >
                      {isLastStep
                        ? intl({
                            id: 'confirm',
                          })
                        : intl({
                            id: 'continue',
                          })}
                    </ButtonLoading>
                  </div>
                </>
              )) || (
                <div>
                  <FormattedMessage id='wizardForm.noService' />
                </div>
              )}
            </div>
          </Container>
          {activeStep === TIMESLOT_STEP && <TimeZonePopup />}
          <TicketSettingModal />
          <SlotTakenWarning open={isSlotTaken} />
          <PausedQueueWarning />
          <TooManyPeopleWarning />

          <ConfirmAppointment
            show={showConfirmModal}
            onClose={() => {
              setShowConfirmModal(false)
            }}
          />

          <PushNotification
            show={showNotificationModal}
            onClose={() => {
              setShowNotificationModal(false)
              setShowConfirmModal(true)
            }}
          />
        </>
      )}
    </>
  )
}
